import React from 'react';
import { AppLayout } from '@/components/layout/AppLayout';
import { MOCK_COLLABORATORS } from '@shared/mock-data';
import { cn } from '@/lib/utils';

export function CollaboratorsPage() {
  return (
    <AppLayout>
      <div className="flex-1 bg-surface-container-low overflow-y-auto">
        <header className="bg-surface border-b border-outline-variant px-4 md:px-margin-page py-12">
          <div className="max-w-max-width-doc mx-auto">
             <span className="font-label-caps text-label-caps text-primary uppercase tracking-widest mb-2 block">Governance</span>
             <h1 className="font-h1 text-h1 text-on-surface mb-4">The Scriptorium Team</h1>
             <p className="font-body-lg text-body-lg text-on-surface-variant max-w-2xl">
               Manage the scribes and editors assigned to your manuscripts. High-density collaboration requires strict role definitions.
             </p>
          </div>
        </header>

        <main className="px-4 md:px-margin-page py-12">
          <div className="max-w-max-width-doc mx-auto space-y-12">
            {/* Stats / Chips */}
            <div className="flex flex-wrap gap-3">
              <div className="px-4 py-2 bg-primary-container text-on-primary-container rounded-full flex items-center gap-2 border border-primary-container/20">
                <span className="material-symbols-outlined text-[18px]">edit</span>
                <span className="font-label-caps text-[10px] uppercase tracking-wider">3 Active Scribes</span>
              </div>
              <div className="px-4 py-2 bg-secondary-container text-on-secondary-container rounded-full flex items-center gap-2 border border-secondary-container/20">
                <span className="material-symbols-outlined text-[18px]">verified</span>
                <span className="font-label-caps text-[10px] uppercase tracking-wider">12 Pending Reviews</span>
              </div>
              <div className="px-4 py-2 bg-tertiary-container text-on-tertiary-container rounded-full flex items-center gap-2 border border-tertiary-container/20">
                <span className="material-symbols-outlined text-[18px]">psychology</span>
                <span className="font-label-caps text-[10px] uppercase tracking-wider">High-Density Editors</span>
              </div>
            </div>

            {/* Collaborator List */}
            <section className="bg-surface border border-outline-variant rounded-2xl overflow-hidden shadow-sm">
              <div className="px-6 py-4 bg-surface-container-low border-b border-outline-variant flex justify-between items-center">
                <h2 className="font-label-caps text-label-caps text-on-surface uppercase tracking-widest">Active Personnel</h2>
                <span className="material-symbols-outlined text-outline">more_vert</span>
              </div>
              
              <div className="divide-y divide-outline-variant">
                {MOCK_COLLABORATORS.map((user) => (
                  <div key={user.id} className="p-6 flex flex-col md:flex-row md:items-center justify-between gap-4 hover:bg-surface-container-low transition-colors">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full border-2 border-outline-variant overflow-hidden p-0.5">
                        <img src={user.avatar} alt={user.name} className="w-full h-full object-cover rounded-full" />
                      </div>
                      <div>
                        <h3 className="font-h3 text-h3 text-on-surface text-[18px] mb-0.5">{user.name}</h3>
                        <p className="font-body-md text-body-md text-on-surface-variant text-sm">{user.email}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-6">
                      <div className="flex items-center gap-2">
                         <span className={cn(
                           "w-2 h-2 rounded-full",
                           user.status === 'Active' ? "bg-teal" : "bg-gold"
                         )}></span>
                         <span className="font-label-caps text-[10px] text-on-surface-variant uppercase tracking-widest">{user.status}</span>
                      </div>
                      
                      <div className="px-4 py-2 rounded bg-surface-container border border-outline-variant font-label-caps text-[10px] uppercase tracking-widest min-w-[100px] text-center">
                        {user.role}
                      </div>

                      <button className="p-2 text-outline hover:text-primary transition-colors">
                        <span className="material-symbols-outlined">settings</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Invite Section */}
            <section className="bg-inverse-surface text-inverse-on-surface rounded-2xl p-8 md:p-12 shadow-2xl relative overflow-hidden">
               <div className="absolute top-0 right-0 w-64 h-64 bg-primary opacity-5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
               <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                  <div className="space-y-4">
                    <h2 className="font-h2 text-h2 text-[32px] italic">Summon New Talent</h2>
                    <p className="font-body-md text-body-md opacity-80 leading-relaxed">
                      Issue a formal invitation to a new scribe or editor. They will be granted limited access to the forge until identity verification is complete.
                    </p>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1 relative">
                       <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 opacity-40">mail</span>
                       <input 
                         type="email" 
                         placeholder="scribe@docforge.ai" 
                         className="w-full bg-surface/10 border border-white/20 rounded-lg py-4 pl-12 pr-4 font-body-md text-white placeholder:text-white/30 focus:outline-none focus:ring-1 focus:ring-primary"
                       />
                    </div>
                    <button className="bg-primary text-on-primary px-8 py-4 rounded-lg font-label-caps text-label-caps uppercase tracking-widest shadow-lg hover:shadow-primary/20 hover:-translate-y-0.5 transition-all active:translate-y-0">
                      Send Summon
                    </button>
                  </div>
               </div>
            </section>
          </div>
        </main>
      </div>
    </AppLayout>
  );
}